import { PGA_COM_HOST } from 'env'
import React, { useState } from 'react'
import { get } from 'lodash'
import { DateTime } from 'luxon'
import { useQuery } from '@apollo/client'
import { useRouteMatch } from 'react-router-dom'
import { PageHeader } from 'components/page-header'
import SectionEmptyState from 'components/section-empty-state'
import { TonalButton } from 'components/empty-page-state'
import GET_GROUP_EVENTS from './query'
import EventsDisplay from './events-display'
import LinearProgress from '@material-ui/core/LinearProgress'
import Error from 'components/error'
import { Tabs, Tab } from 'components/tabs'
import { parseEvents } from 'utils/eventUtils'
import CelebrationOutlinedIcon from 'components/material-symbol/icons/Celebration'
import SearchIcon from 'components/material-symbol/icons/Search'
import SubscribeDialog from './subscribe-dialog'
import SyncIcon from 'components/material-symbol/icons/Sync'
import IconButton from '@material-ui/core/IconButton'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useTheme, makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'

const useStyles = makeStyles(theme => ({
  button: {
    display: 'inherit',
    minWidth: 'max-content',
  },
  buttonsWrapper: {
    display: 'flex',
    marginRight: 0,
    width: '100%',
    paddingLeft: '0.5rem',
    justifyContent: 'space-around',
    gap: '10px',
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
      justifyContent: 'end',
    },
    [theme.breakpoints.down('xs')]: {
      paddingLeft: 0,
      justifyContent: 'center',
    },
  },
  emptyStateContent: {
    padding: theme.spacing(4, 2),
  },
}))

const EventsPageData = ({ selectedTab }) => {
  const classes = useStyles()
  const { data, loading, error } = useQuery(GET_GROUP_EVENTS, {
    variables: {
      orderBy: { field: 'START_DATE_TIME', direction: 'ASC' },
    },
  })

  if (loading) return <LinearProgress color="primary" />
  if (error) return <Error />

  const events = get(data, 'currentUser.groupEvents', [])
  const groupEvents = parseEvents(events)

  const upcomingEvents = groupEvents.filter(
    b =>
      DateTime.fromISO(b.seriesEndDateTime) >= DateTime.local().startOf('day'),
  )
  const pastEvents = groupEvents.filter(
    b =>
      DateTime.fromISO(b.seriesEndDateTime) < DateTime.local().startOf('day'),
  )

  const currentlyViewingEvents =
    selectedTab === 'upcoming' ? upcomingEvents : pastEvents

  return (
    <>
      {currentlyViewingEvents.length ? (
        <EventsDisplay events={currentlyViewingEvents} />
      ) : (
        <Box className={classes.emptyStateContent}>
          <SectionEmptyState
            Icon={CelebrationOutlinedIcon}
            title={`No ${
              selectedTab === 'upcoming' ? 'Upcoming' : 'Past'
            } Events`}
            caption="Events you register for will show up here."
            bordered={false}
            action={
              <TonalButton
                startIcon={<SearchIcon color="primary" />}
                onClick={() => window.open(`${PGA_COM_HOST}/things-to-do`)}
              >
                Find Programs
              </TonalButton>
            }
          />
        </Box>
      )}
    </>
  )
}

const EventsPage = () => {
  const [selectedTab, setSelectedTab] = useState('upcoming')
  const { path } = useRouteMatch()
  const isMobile = useMediaQuery(useTheme().breakpoints.down('xs'))
  const classes = useStyles()

  const actionButton = (
    <SubscribeDialog
      trigger={
        <Button
          variant="outlined"
          color="primary"
          size="medium"
          startIcon={<SyncIcon />}
          className={classes.button}
        >
          Subscribe
        </Button>
      }
    />
  )

  const findProgramsButton = (
    <Button
      variant="contained"
      color="primary"
      size="medium"
      startIcon={<SearchIcon />}
      className={classes.button}
      onClick={() => window.open(`${PGA_COM_HOST}/things-to-do`)}
    >
      Find Programs
    </Button>
  )

  const actions = (
    <Box className={classes.buttonsWrapper}>
      {actionButton}
      {findProgramsButton}
    </Box>
  )

  const crumbs = [
    {
      label: 'My Coach',
      to: path
        .split('/')
        .slice(0, 2)
        .join('/'),
    },
  ]
  return (
    <>
      <PageHeader
        title="Events"
        crumbs={crumbs}
        tabs={
          <Tabs
            value={selectedTab}
            onChange={(_, newValue) => setSelectedTab(newValue)}
          >
            <Tab label="Upcoming" value="upcoming" />
            <Tab label="Past" value="past" />
          </Tabs>
        }
        actions={actions}
        fixedMobileActions={isMobile}
        mobileCornerAction={
          <SubscribeDialog
            trigger={
              <Box mr={2}>
                <IconButton style={{ padding: 0 }} color="primary">
                  <SyncIcon />
                </IconButton>
              </Box>
            }
          />
        }
      />
      <EventsPageData selectedTab={selectedTab} />
    </>
  )
}

export default EventsPage
